/**
 * ExamenesCatalogo - CRUD de tipos de examen
 */

import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Plus,
  MoreHorizontal,
  Pencil,
  CheckCircle,
  XCircle,
  Trash2,
  Search,
  X,
  RefreshCw,
  AlertCircle,
  Ruler,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import {
  DataTableColumnHeader,
  DataTablePagination,
  DataTableViewOptions,
} from "@/components/ui/data-table";
import { ResponsiveTable, useIsMobile } from "@/components/ui/responsive-table";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

import { useApiError } from "@/hooks/use-api-error";
import { useServerDataTable } from "@/hooks/use-server-data-table";
import {
  useDeactivateExamenTipo,
  useActivateExamenTipo,
  useDeleteExamenTipo,
  type ExamenTipoListItem,
} from "@/hooks/use-ordenes";
import { getExamenesTipo } from "@/hooks/use-ordenes";

import { ExamenFormDialog } from "./examen-form-dialog";
import { ValoresReferenciaSheet } from "./valores-referencia-sheet";

const AREAS = [
  "HEMATOLOGIA",
  "QUIMICA",
  "SEROLOGIA",
  "INMUNOLOGIA",
  "URINALISIS",
  "COAGULACION",
  "MICROBIOLOGIA",
  "PARASITOLOGIA",
  "HORMONAS",
  "MARCADORES",
  "DROGAS",
  "ESPECIAL",
] as const;

export function ExamenesCatalogo() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const isMobile = useIsMobile();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [selectedExamen, setSelectedExamen] = useState<ExamenTipoListItem | null>(null);
  const [editExamenId, setEditExamenId] = useState<number | null>(null);
  const [confirmAction, setConfirmAction] = useState<"activate" | "deactivate" | "delete" | null>(
    null
  );
  const [valoresRefExamen, setValoresRefExamen] = useState<ExamenTipoListItem | null>(null);

  const deactivateMutation = useDeactivateExamenTipo({
    onSuccess: () => {
      toast.warning(t("ordenes.examenes.toast.deactivated", { name: selectedExamen?.nombre }));
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });
  const activateMutation = useActivateExamenTipo({
    onSuccess: () => {
      toast.success(t("ordenes.examenes.toast.activated", { name: selectedExamen?.nombre }));
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });
  const deleteMutation = useDeleteExamenTipo({
    onSuccess: () => {
      toast.error(t("ordenes.examenes.toast.deleted", { name: selectedExamen?.nombre }));
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const handleEdit = useCallback((examen: ExamenTipoListItem) => {
    setEditExamenId(examen.id);
    setIsFormOpen(true);
  }, []);

  const handleToggleStatus = useCallback((examen: ExamenTipoListItem) => {
    setSelectedExamen(examen);
    setConfirmAction(examen.is_active ? "deactivate" : "activate");
    setIsConfirmOpen(true);
  }, []);

  const handleDelete = useCallback((examen: ExamenTipoListItem) => {
    setSelectedExamen(examen);
    setConfirmAction("delete");
    setIsConfirmOpen(true);
  }, []);

  const handleConfirmAction = () => {
    if (!selectedExamen || !confirmAction) return;
    if (confirmAction === "activate") activateMutation.mutate(selectedExamen.id);
    else if (confirmAction === "deactivate") deactivateMutation.mutate(selectedExamen.id);
    else if (confirmAction === "delete") deleteMutation.mutate(selectedExamen.id);
    setIsConfirmOpen(false);
    setSelectedExamen(null);
    setConfirmAction(null);
  };

  const handleFormSuccess = (isNew: boolean) => {
    setIsFormOpen(false);
    setEditExamenId(null);
    toast.success(
      isNew ? t("ordenes.examenes.toast.created") : t("ordenes.examenes.toast.updated")
    );
    refetch();
  };

  const columns: ColumnDef<ExamenTipoListItem>[] = useMemo(
    () => [
      {
        accessorKey: "codigo",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("ordenes.examenes.columns.code")} />
        ),
        cell: ({ row }) => <span className="font-mono text-sm">{row.original.codigo}</span>,
      },
      {
        accessorKey: "nombre",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("ordenes.examenes.columns.name")} />
        ),
        cell: ({ row }) => <span className="font-medium">{row.original.nombre}</span>,
      },
      {
        accessorKey: "area",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("ordenes.examenes.columns.area")} />
        ),
        cell: ({ row }) => <Badge variant="outline">{row.original.area_display}</Badge>,
      },
      {
        accessorKey: "precio",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("ordenes.examenes.columns.price")} />
        ),
        cell: ({ row }) => (
          <span className="text-sm">L {Number(row.original.precio).toFixed(2)}</span>
        ),
      },
      {
        accessorKey: "is_active",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("ordenes.examenes.columns.status")} />
        ),
        cell: ({ row }) => (
          <Badge variant={row.original.is_active ? "default" : "secondary"}>
            {row.original.is_active ? t("common.active") : t("common.inactive")}
          </Badge>
        ),
      },
      {
        id: "actions",
        header: () => t("common.actions"),
        cell: ({ row }) => {
          const examen = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleEdit(examen)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  {t("common.edit")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setValoresRefExamen(examen)}>
                  <Ruler className="mr-2 h-4 w-4" />
                  {t("ordenes.examenes.referenceValues")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleToggleStatus(examen)}>
                  {examen.is_active ? (
                    <>
                      <XCircle className="mr-2 h-4 w-4" />
                      {t("common.deactivate")}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      {t("common.activate")}
                    </>
                  )}
                </DropdownMenuItem>
                {!examen.is_active && (
                  <DropdownMenuItem
                    onClick={() => handleDelete(examen)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t("common.delete")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
        enableSorting: false,
      },
    ],
    [t, handleEdit, handleToggleStatus, handleDelete]
  );

  const {
    table,
    isLoading,
    error,
    totalCount,
    refetch,
    searchTerm,
    setSearchTerm,
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
  } = useServerDataTable({
    columns,
    fetchFn: getExamenesTipo,
    queryKey: "examenesTipo",
    storageKey: "examenes_tipo_page_size",
    defaultPageSize: 20,
    filters: [
      { key: "is_active", defaultValue: "all", urlParam: "exam_status" },
      { key: "area", defaultValue: "all" },
    ],
  });

  return (
    <div className="flex flex-col gap-4">
      {/* Actions */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h2 className="text-base sm:text-lg font-semibold">{t("ordenes.examenes.title")}</h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size={isMobile ? "sm" : "default"}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""} ${isMobile ? "" : "mr-2"}`}
            />
            {!isMobile && t("common.refresh")}
          </Button>
          <Button
            size={isMobile ? "sm" : "default"}
            onClick={() => {
              setEditExamenId(null);
              setIsFormOpen(true);
            }}
          >
            <Plus className={`h-4 w-4 ${isMobile ? "" : "mr-2"}`} />
            {isMobile ? t("common.new") : t("ordenes.examenes.newExam")}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 sm:pt-6">
          <div className="space-y-3 sm:space-y-0 sm:flex sm:gap-4">
            {/* Search bar */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t("ordenes.examenes.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Filters - Mobile: Grid layout, Desktop: Flex */}
            <div className="grid grid-cols-2 gap-2 sm:flex sm:gap-2 sm:flex-nowrap">
              <Select value={filters.area} onValueChange={(v) => setFilter("area", v)}>
                <SelectTrigger className="w-full sm:w-[200px]">
                  <SelectValue
                    placeholder={
                      isMobile
                        ? t("ordenes.examenes.areas.short")
                        : t("ordenes.examenes.filterByArea")
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("ordenes.examenes.allAreas")}</SelectItem>
                  {AREAS.map((area) => (
                    <SelectItem key={area} value={area}>
                      {t(`ordenes.examenes.areas.${area}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.is_active} onValueChange={(v) => setFilter("is_active", v)}>
                <SelectTrigger className="w-full sm:w-[150px]">
                  <SelectValue
                    placeholder={isMobile ? t("common.status") : t("ordenes.filterByStatus")}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("ordenes.allStatus")}</SelectItem>
                  <SelectItem value="true">{t("common.active")}</SelectItem>
                  <SelectItem value="false">{t("common.inactive")}</SelectItem>
                </SelectContent>
              </Select>

              {hasActiveFilters && (
                <Button
                  variant="secondary"
                  size="icon"
                  onClick={clearFilters}
                  className="shrink-0"
                  title={t("common.clearFilters")}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-4 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardDescription>{t("ordenes.showingResults", { count: totalCount })}</CardDescription>
            {!isMobile && (
              <DataTableViewOptions
                table={table}
                columnLabels={{
                  codigo: t("ordenes.examenes.columns.code"),
                  nombre: t("ordenes.examenes.columns.name"),
                  area: t("ordenes.examenes.columns.area"),
                  precio: t("ordenes.examenes.columns.price"),
                  is_active: t("ordenes.examenes.columns.status"),
                }}
              />
            )}
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveTable
            table={table}
            columns={columns}
            isLoading={isLoading}
            emptyMessage={t("ordenes.examenes.noExamenes")}
            hideOnTablet={["precio"]}
            renderMobileCard={(examen) => (
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm">{examen.codigo}</span>
                  <Badge variant={examen.is_active ? "default" : "secondary"}>
                    {examen.is_active ? t("common.active") : t("common.inactive")}
                  </Badge>
                </div>
                <p className="font-medium">{examen.nombre}</p>
                <div className="flex items-center justify-between text-sm">
                  <Badge variant="outline">{examen.area_display}</Badge>
                  <span>L {Number(examen.precio).toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-end mt-1">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEdit(examen)}>
                        <Pencil className="mr-2 h-4 w-4" />
                        {t("common.edit")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setValoresRefExamen(examen)}>
                        <Ruler className="mr-2 h-4 w-4" />
                        {t("ordenes.examenes.referenceValues")}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleToggleStatus(examen)}>
                        {examen.is_active ? (
                          <>
                            <XCircle className="mr-2 h-4 w-4" />
                            {t("common.deactivate")}
                          </>
                        ) : (
                          <>
                            <CheckCircle className="mr-2 h-4 w-4" />
                            {t("common.activate")}
                          </>
                        )}
                      </DropdownMenuItem>
                      {!examen.is_active && (
                        <DropdownMenuItem
                          onClick={() => handleDelete(examen)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          {t("common.delete")}
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            )}
          />
          <DataTablePagination table={table} totalRows={totalCount} />
        </CardContent>
      </Card>

      {/* Dialogs */}
      <ExamenFormDialog
        open={isFormOpen}
        onOpenChange={(open: boolean) => {
          setIsFormOpen(open);
          if (!open) setEditExamenId(null);
        }}
        examenId={editExamenId}
        onSuccess={handleFormSuccess}
      />

      <ConfirmDialog
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        title={
          confirmAction === "activate"
            ? t("ordenes.examenes.confirmActivate")
            : confirmAction === "delete"
              ? t("ordenes.examenes.confirmDelete")
              : t("ordenes.examenes.confirmDeactivate")
        }
        description={
          confirmAction === "activate"
            ? t("ordenes.examenes.confirmActivateDesc", { name: selectedExamen?.nombre })
            : confirmAction === "delete"
              ? t("ordenes.examenes.confirmDeleteDesc", { name: selectedExamen?.nombre })
              : t("ordenes.examenes.confirmDeactivateDesc", { name: selectedExamen?.nombre })
        }
        onConfirm={handleConfirmAction}
        variant={
          confirmAction === "deactivate" || confirmAction === "delete" ? "destructive" : "default"
        }
      />

      <ValoresReferenciaSheet
        open={!!valoresRefExamen}
        onOpenChange={(open: boolean) => {
          if (!open) setValoresRefExamen(null);
        }}
        examen={valoresRefExamen}
      />
    </div>
  );
}
